// Module-level token position animator. Lives outside React so Firestore
// snapshot re-renders cannot reset in-flight tweens.
//
// An animation is a sequence of waypoints (grid coords, can be fractional on
// the first entry when a drag ends mid-cell). Progress is linear along the
// combined path so turns around walls move at a constant pace.

const animations = new Map(); // id -> { waypoints, totalLen, segLens, startTs, durationMs }
const teleports  = new Map(); // id -> { srcX, srcY, dstX, dstY, startTs, outMs, holdMs, inMs }
const listeners = new Set();
let version = 0;
let rafId = null;

const MS_PER_CELL = 240;

// Teleport flash phases. Tunable per-call via startTeleport options.
const TELEPORT_OUT_MS  = 180;
const TELEPORT_HOLD_MS = 60;
const TELEPORT_IN_MS   = 180;

function tick() {
  version++;
  const now = performance.now();
  // Safety GC only — ordinary retirement happens in getDisplayPos once the
  // committed truth has caught up with the last waypoint. Keeping the entry
  // alive past its duration prevents a one-frame snap to committed coords
  // when the animation ends before the Firestore write round-trips back.
  for (const [id, a] of animations) {
    if (now - a.startTs > a.durationMs + 3000) animations.delete(id);
  }
  for (const [id, tp] of teleports) {
    const total = tp.outMs + tp.holdMs + tp.inMs;
    if (now - tp.startTs > total + 3000) teleports.delete(id);
  }
  listeners.forEach((cb) => cb());
  rafId = (animations.size || teleports.size) ? requestAnimationFrame(tick) : null;
}

function buildLengths(waypoints) {
  const segLens = [];
  let totalLen = 0;
  for (let i = 0; i < waypoints.length - 1; i++) {
    const dx = waypoints[i + 1].x - waypoints[i].x;
    const dy = waypoints[i + 1].y - waypoints[i].y;
    const len = Math.hypot(dx, dy);
    segLens.push(len);
    totalLen += len;
  }
  return { segLens, totalLen };
}

export function startAnimation(id, waypoints, msPerCell = MS_PER_CELL) {
  if (!waypoints || waypoints.length < 2) return;
  const { segLens, totalLen } = buildLengths(waypoints);
  if (totalLen === 0) return;
  animations.set(id, {
    waypoints,
    segLens,
    totalLen,
    startTs: performance.now(),
    durationMs: Math.max(120, totalLen * msPerCell),
  });
  if (!rafId) rafId = requestAnimationFrame(tick);
}

export function getAnimation(id) {
  return animations.get(id) || null;
}

export function isAnimating(id) {
  return animations.has(id);
}

// ── Teleport flash ────────────────────────────────────────────────────────
// Used for portal/stairs/pit/trapdoor moves: fade out at source, brief hold
// invisible, fade in at destination. Cancels any walk animation for this id.
export function startTeleport(id, src, dst, opts = {}) {
  animations.delete(id);
  teleports.set(id, {
    srcX:    src.x,
    srcY:    src.y,
    dstX:    dst.x,
    dstY:    dst.y,
    startTs: performance.now(),
    outMs:   opts.outMs  ?? TELEPORT_OUT_MS,
    holdMs:  opts.holdMs ?? TELEPORT_HOLD_MS,
    inMs:    opts.inMs   ?? TELEPORT_IN_MS,
  });
  if (!rafId) rafId = requestAnimationFrame(tick);
}

export function isTeleporting(id) {
  return teleports.has(id);
}

// Returns { x, y, opacity } if the token is in a teleport flash; null otherwise.
// Retires the entry once committed truth matches the destination.
function getTeleportFlash(id, committedX, committedY) {
  const tp = teleports.get(id);
  if (!tp) return null;
  const t = performance.now() - tp.startTs;
  const total = tp.outMs + tp.holdMs + tp.inMs;
  if (t >= total) {
    if (committedX === tp.dstX && committedY === tp.dstY) {
      teleports.delete(id);
      return null;
    }
    return { x: tp.dstX, y: tp.dstY, opacity: 1 };
  }
  if (t < tp.outMs) {
    return { x: tp.srcX, y: tp.srcY, opacity: Math.max(0, 1 - t / tp.outMs) };
  }
  if (t < tp.outMs + tp.holdMs) {
    return { x: tp.dstX, y: tp.dstY, opacity: 0 };
  }
  const tin = t - tp.outMs - tp.holdMs;
  return { x: tp.dstX, y: tp.dstY, opacity: Math.min(1, tin / tp.inMs) };
}

export function getTeleportOpacity(id, committedX, committedY) {
  const fl = getTeleportFlash(id, committedX, committedY);
  return fl ? fl.opacity : 1;
}

export function getDisplayPos(id, committedX, committedY) {
  const fl = getTeleportFlash(id, committedX, committedY);
  if (fl) return { x: fl.x, y: fl.y };
  const a = animations.get(id);
  if (!a) return { x: committedX, y: committedY };
  const t = Math.min(1, (performance.now() - a.startTs) / a.durationMs);
  if (t >= 1) {
    const last = a.waypoints[a.waypoints.length - 1];
    // Retire the animation once the committed truth matches the final waypoint
    // — avoids a flash back to stale coords if Firestore hasn't caught up yet.
    if (committedX === last.x && committedY === last.y) {
      animations.delete(id);
      return { x: committedX, y: committedY };
    }
    return { x: last.x, y: last.y };
  }
  // Walk the segments to find where we are at progress t.
  let target = t * a.totalLen;
  for (let i = 0; i < a.segLens.length; i++) {
    if (target <= a.segLens[i] || i === a.segLens.length - 1) {
      const segT = a.segLens[i] === 0 ? 0 : target / a.segLens[i];
      const p1 = a.waypoints[i];
      const p2 = a.waypoints[i + 1];
      return { x: p1.x + (p2.x - p1.x) * segT, y: p1.y + (p2.y - p1.y) * segT };
    }
    target -= a.segLens[i];
  }
  const last = a.waypoints[a.waypoints.length - 1];
  return { x: last.x, y: last.y };
}

export function subscribe(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function getVersion() {
  return version;
}

// ── Paperdoll animation frame ticker ──────────────────────────────────────
// Global wall-clock frame index so (a) short tweens still play a clean walk
// cycle and (b) all idle tokens share cache keys. A separate interval-based
// ticker drives re-renders when no position tween is running.

const lastDir = new Map(); // tokenId -> 'down' | 'left' | 'right' | 'up'
const frameListeners = new Set();
let frameIntervalId = null;
const FRAME_TICK_MS = 110;
const IDL_MS = 180;
const MOV_MS = 110;
const IDL_FRAMES = 4;
const MOV_FRAMES = 6;

function ensureFrameTicker() {
  if (frameIntervalId) return;
  frameIntervalId = setInterval(() => {
    version++;
    frameListeners.forEach((cb) => cb());
    if (frameListeners.size === 0) {
      clearInterval(frameIntervalId);
      frameIntervalId = null;
    }
  }, FRAME_TICK_MS);
}

export function subscribeFrame(cb) {
  frameListeners.add(cb);
  ensureFrameTicker();
  return () => frameListeners.delete(cb);
}

export function setFacing(id, dir) {
  if (!dir) return;
  lastDir.set(id, dir);
  version++;
  listeners.forEach((cb) => cb());
}

export function getAnimFrame(id, defaultDir) {
  const now = performance.now();
  const a = animations.get(id);
  if (a) {
    const elapsed = Math.min(a.durationMs, now - a.startTs);
    let target = (elapsed / a.durationMs) * a.totalLen;
    let segIdx = 0;
    for (let i = 0; i < a.segLens.length; i++) {
      if (target <= a.segLens[i] || i === a.segLens.length - 1) { segIdx = i; break; }
      target -= a.segLens[i];
    }
    const p1 = a.waypoints[segIdx];
    const p2 = a.waypoints[segIdx + 1];
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    let dir;
    if (Math.abs(dx) >= Math.abs(dy) && dx !== 0) dir = dx > 0 ? 'right' : 'left';
    else if (dy !== 0)                            dir = dy > 0 ? 'down'  : 'up';
    else                                          dir = lastDir.get(id) || 'down';
    lastDir.set(id, dir);
    const frame = Math.floor(now / MOV_MS) % MOV_FRAMES;
    return { anim: 'Mov', dir, frame };
  }
  const dir = lastDir.get(id) || defaultDir || 'down';
  const frame = Math.floor(now / IDL_MS) % IDL_FRAMES;
  return { anim: 'Idl', dir, frame };
}
